import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import Login from '@/components/login/Login.vue'
import MapView from '@/views/MapView.vue'
import HistoryView from '@/views/HistoryView.vue'
import VehiclesView from '@/views/VehiclesView.vue'
import AssetView from '@/views/AssetView.vue'
import AssetDetailsView from '@/views/AssetDetailsView.vue'
import AssetsMapView from '@/views/AssetsMapView.vue'
import ReportsView from '@/views/ReportsView.vue'
import SentGeoView from '@/views/SentGeoView.vue'
import i18n from '@/i18n'
import TachographFilesView from '@/views/TachographFilesView.vue'
import TachoFileAnalysisView from '@/views/TachoFileAnalysisView.vue'
import TachoDriverActivityView from '@/views/TachoDriverActivityView.vue'
import GeoFencingView from '@/views/GeoFencingView.vue'
import Notifications from '@/views/NotificationsView.vue'
import { canAccessAssetModule, canAccessInfrastructure, userDetails } from '@/utils/tools'
import NotificationsSettings from '@/views/NotificationSettingsView.vue'
import TokenManagementView from "@/views/TokenManagementView.vue";
import InfrastructureView from "@/views/InfrastructureView.vue";
import AssetNotificationRulesView from "@/views/AssetNotificationRulesView.vue";
import AssetNotificationHistoryView from "@/views/AssetNotificationHistoryView.vue";
import AssetModbusParamSetView from "@/views/AssetModbusParamSetView.vue";

//Module Form Views
import ModulesFormsIndexView from '@/components/content/_modules/forms/index.vue'
import ModulesFormsListView from '@/components/content/_modules/forms/list.vue'
import ModulesFormsFormView from '@/components/content/_modules/forms/form.vue'
import ModulesFormsReportView from '@/components/content/_modules/forms/report.vue'
import UsersIndexView from '@/components/content/_modules/users/IndexView.vue';
import UsersListView  from '@/components/content/_modules/users/ListView.vue';
import UserFormView   from '@/components/content/_modules/users/FormView.vue';
import AssetHierarchyView from '@/views/AssetHierarchyView.vue'
import AssetGroupsView from '@/views/AssetGroupsView.vue'
import AssetGroupsWithLocationView from '@/views/AssetGroupsWithLocationView.vue'
import WirepasLocationsView from '@/views/WirepasLocationsView.vue'
import WirepasLocationDetailView from '@/views/WirepasLocationDetailView.vue'
import WirepasAssetsView from '@/views/WirepasAssetsView.vue'
import WirepasAssetDetailView from '@/views/WirepasAssetDetailView.vue'
import AssetGroupsAveragesView from '@/views/AssetGroupsAveragesView.vue'
import UserAssetsView from '@/views/UserAssetsView.vue'
import AssetGroupDetailsView from '@/views/AssetGroupDetailsView.vue'
import AssetSchemesView from '@/views/AssetSchemesView.vue'
import AssetSchemePreviewView from '@/views/AssetSchemePreviewView.vue'
import AssetSchemeEditorView from '@/views/AssetSchemeEditorView.vue'
import { getClientBrandingDocumentTitle } from '@/utils/client-branding'

const { t } = i18n.global || i18n
const user = userDetails() || null;

const routes: Array<RouteRecordRaw> = [
    {
        path: '/',
        name: 'Map',
        component: MapView,
        meta: {
            requiresAuth: true,
            title: t('aside.menu.pages.map')
        }
    },
    {
        path: '/login',
        name: 'Login',
        component: Login,
        meta: {
            hideMenu: true,
            title: t('aside.menu.pages.login')
        }
    },
    {
        path: '/history',
        name: 'History',
        component: HistoryView,
        meta: {
            requiresAuth: true,
            title: t('aside.menu.pages.history')
        }
    },
    {
        path: '/vehicles',
        name: 'Vehicles',
        component: VehiclesView,
        meta: {
            requiresAuth: true,
            title: t('aside.menu.pages.vehicles')
        }
    },
    {
        path: '/assets-map',
        name: 'AssetsMap',
        component: AssetsMapView,
        meta: {
            requiresAuth: true,
            roles: ['ROLE_ASSET'],
            title: t('aside.menu.pages.assetsMap')
        }
    },
    {
        path: '/asset-groups',
        name: 'AssetGroups',
        component: AssetGroupsView,
        meta: {
            requiresAuth: true,
            roles: ['ROLE_ASSET'],
            title: t('aside.menu.pages.assetGroups')
        }
    },
    {
        path: '/asset-groups/with-location',
        name: 'AssetGroupsWithLocation',
        component: AssetGroupsWithLocationView,
        meta: {
            requiresAuth: true,
            roles: ['ROLE_ASSET'],
            title: t('aside.menu.pages.assetGroupsWithLocation')
        }
    },
    {
        path: '/asset-schemes',
        name: 'AssetSchemes',
        component: AssetSchemesView,
        meta: {
            requiresAuth: true,
            roles: ['ROLE_ASSET'],
            title: t('aside.menu.pages.assetSchemes')
        }
    },
    {
        path: '/wirepas-locations',
        name: 'WirepasLocations',
        component: WirepasLocationsView,
        meta: { requiresAuth: true, roles: ['ROLE_ASSET'], title: t('aside.menu.pages.wirepasLocations') }
    },
    {
        path: '/wirepas-locations/:id/detail',
        name: 'WirepasLocationDetail',
        component: WirepasLocationDetailView,
        meta: { requiresAuth: true, roles: ['ROLE_ASSET'], title: t('aside.menu.pages.wirepasLocations') },
        props: true,
    },
    {
        path: '/wirepas-assets',
        name: 'WirepasAssets',
        component: WirepasAssetsView,
        meta: { requiresAuth: true, roles: ['ROLE_ASSET'], title: t('aside.menu.pages.wirepasAssets') }
    },
    {
        path: '/wirepas-assets/:id',
        name: 'WirepasAssetDetail',
        component: WirepasAssetDetailView,
        meta: { requiresAuth: true, roles: ['ROLE_ASSET'], title: t('aside.menu.pages.wirepasAssets') },
        props: true,
    },
    {
        path: '/asset-schemes/:id',
        name: 'AssetSchemePreview',
        component: AssetSchemePreviewView,
        meta: {
            requiresAuth: true,
            roles: ['ROLE_ASSET'],
            title: t('aside.menu.pages.assetSchemes')
        },
        props: true
    },
    {
        path: '/asset-schemes/:id/edit',
        name: 'AssetSchemeEdit',
        component: AssetSchemeEditorView,
        meta: {
            requiresAuth: true,
            roles: ['ROLE_ASSET'],
            title: t('aside.menu.pages.assetSchemes')
        },
        props: true
    },
    {
        path: '/asset-hierarchy',
        name: 'AssetHierarchy',
        component: AssetHierarchyView,
        meta: {
            requiresAuth: true,
            roles: ['ROLE_ASSET'],
            title: t('aside.menu.pages.assetHierarchy')
        }
    },
    {
        path: '/user-assets',
        name: 'UserAssets',
        component: UserAssetsView,
        meta: {
            requiresAuth: true,
            roles: ['ROLE_ASSET'],
            title: t('aside.menu.pages.userAssets')
        }
    },
    {
        path: '/assets',
        name: 'Assets',
        component: AssetView,
        meta: {
            requiresAuth: true,
            roles: ['ROLE_ASSET'],
            title: t('aside.menu.pages.assets')
        }
    },
    {
        path: '/assets/groups',
        name: 'AssetsGroupsList',
        component: AssetGroupsAveragesView,
        meta: {
            requiresAuth: true,
            roles: ['ROLE_ASSET'],
            title: t('aside.menu.pages.assetsGroupsList')
        }
    },
    {
        path: '/assets/groups/:id',
        name: 'AssetGroupDetails',
        component: AssetGroupDetailsView,
        meta: {
            requiresAuth: true,
            roles: ['ROLE_ASSET'],
            title: t('aside.menu.pages.assetsGroupsList')
        },
        props: true
    },
    {
        path: '/assets/modbus-params',
        name: 'AssetModbusParamSet',
        component: AssetModbusParamSetView,
        meta: {
            requiresAuth: true,
            roles: ['ROLE_MODUS_PARAMS_SET'],
            parameters: ['HAS_MODBUS_ASSETS'],
            title: t('aside.menu.pages.assetModbusParamSet')
        }
    },
    {
        path: '/assets/:id',
        name: 'AssetDetails',
        component: AssetDetailsView,
        meta: {
            requiresAuth: true,
            roles: ['ROLE_ASSET'],
            title: t('aside.menu.pages.assets')
        },
        props: true
    },
    {
        path: '/reports',
        name: 'Reports',
        component: ReportsView,
        meta: {
            requiresAuth: true,
            title: t('aside.menu.pages.reports')
        }
    },
    {
        path: '/sent-geo',
        name: 'SentGeo',
        component: SentGeoView,
        meta: {
            requiresAuth: true,
            title: t('aside.menu.pages.sentGeo')
        }
    },
    {
        path: '/tachograph-files',
        name: 'TachographFiles',
        component: TachographFilesView,
        meta: {
            requiresAuth: true,
            title: t('aside.menu.pages.tachographFiles')
        }
    },
    {
        path: '/tacho-analysis',
        name: 'TachoFileAnalysis',
        component: TachoFileAnalysisView,
        meta: {
            requiresAuth: true,
            title: t('aside.menu.pages.tachoAnalysis')
        }
    },
    {
        path: '/tacho-analysis/driver/:driverId',
        name: 'TachoDriverActivity',
        component: TachoDriverActivityView,
        props: true,
        meta: {
            requiresAuth: true,
            title: t('aside.menu.pages.tachoAnalysis')
        }
    },
    {
        path: '/geofencing',
        name: 'Geofencing',
        component: GeoFencingView,
        meta: {
            requiresAuth: true,
            title: t('aside.menu.pages.geofencing')
        }
    },
    {
        path: '/notifications/history',
        name: 'Notifications',
        component: Notifications,
        meta: {
            requiresAuth: true,
            title: t('aside.menu.pages.notificationsHistory')
        }
    },
    {
        path: '/notifications/settings',
        name: 'NotificationSettings',
        component: NotificationsSettings,
        meta: {
            requiresAuth: true,
            title: t('aside.menu.pages.notificationSettings')
        }
    },
    {
        path: '/notifications/asset-rules',
        name: 'AssetNotificationRules',
        component: AssetNotificationRulesView,
        meta: {
            requiresAuth: true,
            roles: ['ROLE_ASSET'],
            title: t('aside.menu.pages.assetNotificationRules')
        }
    },
    {
        path: '/notifications/asset-history',
        name: 'AssetNotificationHistory',
        component: AssetNotificationHistoryView,
        meta: {
            requiresAuth: true,
            roles: ['ROLE_ASSET'],
            title: t('aside.menu.pages.assetNotificationHistory')
        }
    },
    {
        path: '/token-management',
        name: 'TokenManagement',
        component: TokenManagementView,
        meta: {
            requiresAuth: true,
            requiresAdminOrBoss: true,
            roles: ['ROLE_ADMIN', 'ROLE_BOSS'],
            title: t('aside.menu.pages.tokenManagement')
        }
    },
    {
        path: '/infrastructure',
        name: 'Infrastructure',
        component: InfrastructureView,
        meta: {
            requiresAuth: true,
            requiresAdminOrBoss: true,
            // ROLE_ASSET_OWNER/ROLE_ASSET_ADMIN also needed here, not just ROLE_ADMIN/ROLE_BOSS --
            // this is the final OR-based role check, reached after the earlier asset-branch
            // isInfrastructurePath bypass. Without it, a ROLE_ASSET_ADMIN-only user (no ROLE_BOSS)
            // would pass that bypass but then get redirected away right here.
            roles: ['ROLE_ADMIN', 'ROLE_BOSS', 'ROLE_ASSET_OWNER', 'ROLE_ASSET_ADMIN'],
            title: t('aside.menu.pages.infrastructure')
        }
    },
    {
        path: '/modules/forms',
        name: 'ModulesFormIndexView',
        component: ModulesFormsIndexView,
        meta: {
            requiresAuth: true,
            //roles: ['ROLE_ADMIN'],
            title: t('aside.menu.pages.modules.forms'),
        }
    },
    {
        path: '/modules/forms/list',
        name: 'ModulesFormsListView',
        component: ModulesFormsListView,
        meta: {
            requiresAuth: true,
            roles: ['ROLE_ADMIN', 'ROLE_BOSS'],
            title: t('aside.menu.pages.modules.forms'),
        }
    },
    {
        path: '/modules/forms/report',
        name: 'ModulesFormsReportView',
        component: ModulesFormsReportView,
        meta: {
            requiresAuth: true,
            roles: ['ROLE_ADMIN', 'ROLE_BOSS'],
            title: t('aside.menu.pages.modules.formsReport'),
        }
    },
    {
        path: '/modules/forms/form/:catchAll',
        name: 'ModulesFormsFormView',
        component: ModulesFormsFormView,
        meta: {
            requiresAuth: true,
            //roles: ['ROLE_ADMIN', 'ROLE_BOSS'],
            title: t('aside.menu.pages.modules.forms'),
        }
    },
    {
        path: '/users',
        component: UsersIndexView,
        meta: {
            requiresAuth: true,
            roles: ['ROLE_ADMIN', 'ROLE_BOSS'],
            title: t('aside.menu.pages.users'),
        },
        children: [
            {path: '', name: 'UsersList', component: UsersListView},
            {path: 'create', name: 'UserCreate', component: UserFormView},
            {path: ':id/edit', name: 'UserEdit', component: UserFormView, props: true},
        ],
    },
    {
        path: '/:catchAll(.*)',
        name: '404',
        redirect: '/'
    }
]

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
    scrollBehavior(to, from, savedPosition) {
        // Always scroll to top when navigating to a new route
        if (savedPosition) {
            return savedPosition;
        } else {
            return { top: 0, left: 0 };
        }
    }
})

router.beforeEach((to, from, next) => {

    if (!to.meta.requiresAuth) {
        return next();
    }

    const currentUser = userDetails();

    if (!currentUser) {
        return next({ path: '/login', query: { redirect: to.fullPath } });
    }

    const roles: string[] = currentUser?.roles ?? [];

    if (roles.includes('ROLE_FORMS_MOBILE') && to.path === '/') {
        return next('/modules/forms');
    }

    const hasAssetRole = canAccessAssetModule(roles);
    // Full superadmin (ROLE_ADMIN + ROLE_BOSS) gets unrestricted access, even though
    // canAccessAssetModule() also returns true for them -- that helper answers "can read
    // asset data", not "is limited to asset data". Without this, a superadmin would get
    // redirected away from admin-only pages like /token-management or /infrastructure by
    // the asset-only branch below, since those paths aren't in allowedPathsForAsset.
    const isSuperAdmin = roles.includes('ROLE_ADMIN') && roles.includes('ROLE_BOSS');

    if (!isSuperAdmin && hasAssetRole) {
        // User with ROLE_ASSET: only asset-related paths allowed
        const allowedPathsForAsset = ['/assets-map', '/assets', '/notifications/asset-rules', '/notifications/asset-history', '/asset-hierarchy', '/asset-groups', '/user-assets', '/asset-schemes', '/wirepas-locations', '/wirepas-assets', '/wirepas-assets/'];
        const isAssetDetailsPath = to.path.startsWith('/assets/');
        const isAssetGroupsPath = to.path.startsWith('/asset-groups');
        const isAssetSchemesPath = to.path.startsWith('/asset-schemes');

        const isWirepasDetailPath = to.path.startsWith('/wirepas-assets/') || to.path.startsWith('/wirepas-locations/')
        // /infrastructure is a company-privileged page, not a plain asset-module route: a
        // company's asset owner/admin (not just any ROLE_ASSET user) can reach it too.
        const isInfrastructurePath = to.path === '/infrastructure'
            && (roles.includes('ROLE_ASSET_OWNER') || roles.includes('ROLE_ASSET_ADMIN'));
        if (!allowedPathsForAsset.includes(to.path) && !isAssetDetailsPath && !isAssetGroupsPath && !isAssetSchemesPath && !isWirepasDetailPath && !isInfrastructurePath) {
            return next('/assets-map');
        }
    } else if (!isSuperAdmin && !hasAssetRole) {
        // User without ROLE_ASSET: block all asset-related paths
        const isAssetPath = to.path === '/assets' ||
                            to.path === '/assets-map' ||
                            to.path === '/asset-hierarchy' ||
                            to.path === '/user-assets' ||
                            to.path === '/notifications/asset-rules' ||
                            to.path === '/notifications/asset-history' ||
                            to.path.startsWith('/assets/') ||
                            to.path.startsWith('/asset-groups') ||
                            to.path.startsWith('/asset-schemes') ||
                            to.path === '/wirepas-locations' ||
                            to.path === '/wirepas-assets';

        if (isAssetPath) {
            return next('/');
        }
    }

    // /infrastructure needs precise AND logic (superadmin, or a company's asset owner/admin
    // who also has ROLE_ASSET) -- the generic OR-based meta.roles check below would wrongly
    // admit a ROLE_BOSS-only user (no ROLE_ASSET, no ROLE_ADMIN): they're already correctly
    // hidden from the menu for this page (Menu.vue), so a direct hit would only land them on
    // a page whose API calls then 403, instead of being redirected away like everywhere else.
    if (to.path === '/infrastructure') {
        if (!canAccessInfrastructure(roles)) {
            return next('/');
        }
    }

    // Check role requirements for pages that have them
    const requiredRoles = to.meta.roles as string[] | undefined;
    const hasRequiredRole = !Array.isArray(requiredRoles)
        || roles.some(role => requiredRoles.includes(role))
        || (requiredRoles.includes('ROLE_ASSET') && canAccessAssetModule(roles));

    if (!hasRequiredRole) {
        return next('/');
    }

    // Check parameter requirements for pages that have them
    const requiredParams = to.meta.parameters as string[] | undefined;
    if (Array.isArray(requiredParams)) {
        const userParams: string[] = (currentUser?.parameters ?? []).map((p: any) => p.name);
        if (!requiredParams.some(p => userParams.includes(p))) {
            return next('/');
        }
    }

    document.title = getClientBrandingDocumentTitle(to.meta.title as string | undefined);

    next();
});

export default router
